import ShareIcon from '@mui/icons-material/Share.js';
/* eslint-disable import/extensions */
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { COMPANY_PROJECT_TYPES_VALUES, queryString } from '@woovi/shared';
import { BoxFlex, ActionButton } from '@woovi/ui';
import { convertToCurrency, maskMoney } from '@woovi/utils';

import type { PaymentLinkShareButton_charge$key } from './__generated__/PaymentLinkShareButton_charge.graphql.ts';
import type { PaymentLinkShareButton_pixQrCode$key } from './__generated__/PaymentLinkShareButton_pixQrCode.graphql.ts';
import { useChargePaymentMethod } from '../../charge/useChargePaymentMethod.tsx';

const { stringify } = queryString;

type PaymentLinkShareButtonProps = {
  charge: PaymentLinkShareButton_charge$key;
  pixQrCode: PaymentLinkShareButton_pixQrCode$key;
};

const PaymentLinkShareButton = (
  props: PaymentLinkShareButtonProps,
): ReactNode => {
  const { t } = useTranslation();

  const charge = useFragment<PaymentLinkShareButton_charge$key>(
    graphql`
      fragment PaymentLinkShareButton_charge on Charge {
        company {
          nameFriendly
          projectType
        }
        paymentLinkUrl
        cashback {
          value
        }
        customer {
          phone
        }
        paymentMethods {
          ... on PaymentMethodPix {
            value
            valueWithDiscount
          }
        }
        ...useChargePaymentMethod_charge
      }
    `,
    props.charge,
  );

  const pixQrCode = useFragment<PaymentLinkShareButton_pixQrCode$key>(
    graphql`
      fragment PaymentLinkShareButton_pixQrCode on PixQrCode {
        company {
          nameFriendly
          projectType
        }
        value
        paymentLinkUrl
      }
    `,
    props.pixQrCode,
  );

  const { pixCob } = useChargePaymentMethod(charge);

  const getMoney = () => {
    if (pixQrCode) {
      return pixQrCode.value;
    }

    const chargeValue = pixCob.valueWithDiscount ?? pixCob.value ?? 0;

    return chargeValue > 0 ? maskMoney(chargeValue / 100) : '';
  };

  const getCompany = () => {
    if (pixQrCode) {
      return pixQrCode.company;
    }

    return charge.company;
  };

  const getUrl = () => {
    if (pixQrCode) {
      return pixQrCode.paymentLinkUrl;
    }

    return charge.paymentLinkUrl;
  };

  const title = t('Transfer {{money}} to {{merchantName}}', {
    money: getMoney(),
    merchantName: getCompany().nameFriendly,
  });

  const url = getUrl();

  const getShareText = () => {
    const projectName = !!getCompany().projectType
      ? COMPANY_PROJECT_TYPES_VALUES[getCompany().projectType].name
      : 'OpenPix';

    if (pixQrCode || !charge.cashback) {
      return t(
        'Pay {{money}} to {{merchantName}} through {{projectName}}. {{link}}',
        {
          money: getMoney(),
          merchantName: getCompany().nameFriendly,
          projectName,
          link: url ?? '',
        },
      );
    }

    const earnCashbackValue = convertToCurrency(charge.cashback.value / 100);

    return t(
      'Pay {{money}} to {{merchantName}} through {{projectName}} and win {{earnCashbackValue}} of cashback. {{link}}',
      {
        money: getMoney(),
        merchantName: getCompany().nameFriendly,
        projectName,
        earnCashbackValue,
        link: url ?? '',
      },
    );
  };

  const text = getShareText();

  const shareOpen = () => {
    const isDesktop = !navigator.userAgent.match(/Android|iPhone|iPad|iPod/i);

    const phone = charge?.customer?.phone;

    const queryString = stringify({ text, phone });

    window.open(
      (isDesktop ? 'https://api.whatsapp.com/send?' : 'whatsapp://send?') +
        queryString,
    );
  };

  const share = async () => {
    const isWindows = !!navigator.userAgent.match(/Windows/i);
    const isMac = !!navigator.userAgent.match(/Macintosh/i);
    const isSafari = !!navigator.userAgent.match(/Safari/i);

    if (isWindows || (isMac && isSafari)) {
      shareOpen();

      return;
    }

    if (!!navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
      } catch (err) {
        // eslint-disable-next-line
        console.log('share err', err);

        shareOpen();
      }
    } else {
      shareOpen();
    }
  };

  const getLabel = () => {
    if (pixQrCode) {
      return t('Share QR Code');
    }

    return t('Share charge');
  };

  return (
    <BoxFlex
      sx={{
        flex: 1,
      }}
    >
      <ActionButton
        sx={(theme) => ({
          width: '100%',
          color: theme.palette.tertiary.main,
          '&:hover': {
            color: theme.palette.tertiary.main,
          },
          '&:focus': {
            color: theme.palette.tertiary.main,
            backgroundColor: theme.palette.common.white,
          },
        })}
        variant='outlined'
        color={'tertiary'}
        startIcon={<ShareIcon color={'tertiary'} />}
        onClick={share}
        size={'large'}
      >
        <Typography
          as='span'
          sx={(theme) => ({
            textTransform: 'uppercase',
            color: theme.palette.tertiary.main,
          })}
        >
          {getLabel()}
        </Typography>
      </ActionButton>
    </BoxFlex>
  );
};

export default PaymentLinkShareButton;
